// src/components/common/AddressForm/AddressForm.jsx

import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import styles from './AddressForm.module.css';
import { FiX, FiPlus } from 'react-icons/fi';

const AddressForm = ({
    address,
    onChange,
    onSave,
    onCancel,
    isSaving = false,
    addressTypes = [],
    roles = [],
    country = 'BR',
    title = 'New Address',
    saveText = 'Add Address',
    cancelText = 'Cancel',
    isImporter = false,
    importerCompanyName = '',
    onImporterCompanyChange = null,
}) => {
    const [localAddress, setLocalAddress] = useState(address);
    const [newContact, setNewContact] = useState({ name: '', email: '', cargo: '' });
    const [allStates, setAllStates] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '40px',
            borderColor: 'var(--border-color, #dee2e6)',
            boxShadow: 'none',
            '&:hover': { borderColor: 'var(--primary-color, #007bff)' }
        }),
        placeholder: (base) => ({ ...base, color: '#6c757d' })
    };

    useEffect(() => {
        setLocalAddress(address);
    }, [address]);

    useEffect(() => {
        if (country === 'BR') {
            setLoadingStates(true);
            fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
                .then(res => res.json())
                .then(data => {
                    const formattedStates = data.map(uf => ({ value: uf.sigla, label: uf.nome }));
                    setAllStates(formattedStates);
                })
                .catch(err => console.error("Erro ao buscar UFs:", err))
                .finally(() => setLoadingStates(false));
        } else {
            setAllStates([]);
            setAllCities([]);
        }
    }, [country]);

    useEffect(() => {
        const selectedUF = localAddress.uf;
        if (selectedUF && country === 'BR') {
            setLoadingCities(true);
            fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedUF}/municipios?orderBy=nome`)
                .then(res => res.json())
                .then(data => {
                    const formattedCities = data.map(city => ({ value: city.nome, label: city.nome }));
                    setAllCities(formattedCities);
                })
                .catch(err => console.error("Erro ao buscar Cidades:", err))
                .finally(() => setLoadingCities(false));
        }
    }, [localAddress.uf, country]);

    const handleChange = (field, value) => {
        const updated = { ...localAddress, [field]: value };
        setLocalAddress(updated);
        if (onChange) onChange(updated);
    };

    const handleContactChange = (field, value) => {
        setNewContact(prev => ({ ...prev, [field]: value }));
    };

    const handleAddContact = () => {
        if (!newContact.name) return;
        const updatedContacts = [...(localAddress.contacts || []), newContact];
        handleChange('contacts', updatedContacts);
        setNewContact({ name: '', email: '', cargo: '' });
    };

    const handleRemoveContact = (index) => {
        const updatedContacts = (localAddress.contacts || []).filter((_, i) => i !== index);
        handleChange('contacts', updatedContacts);
    };

    const handleFetchCep = (cepValue) => {
        const cleanCep = cepValue.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
                .then(res => res.json())
                .then(data => {
                    if (!("erro" in data)) {
                        handleChange('logradouro', data.logradouro || '');
                        handleChange('bairro', data.bairro || '');
                        handleChange('uf', data.uf || '');
                        handleChange('cidade', data.localidade || '');
                        handleChange('complemento', data.complemento || '');
                    }
                })
                .catch(err => console.error("Erro ao buscar CEP:", err));
        }
    };

    const typeAddressOptions = useMemo(() =>
        addressTypes.map(t => ({ value: t.id, label: t.name }))
        , [addressTypes]);

    const rolesOptions = useMemo(() =>
        roles.map(r => ({ value: r.id, label: r.name }))
        , [roles]);

    const selectedType = typeAddressOptions.find(o => o.value === localAddress.tipo) || null;
    const selectedUF = allStates.find(o => o.value === localAddress.uf) || null;
    const selectedCity = allCities.find(o => o.value === localAddress.cidade) || null;

    return (
        <div className={styles.addressFormCard}>
            <div className={styles.addressFormHeader}>
                <span className={styles.addressFormIcon}>📍</span>
                <h4>{title}</h4>
                {isImporter && <span className={styles.importerBadge}>🏭 Importador</span>}
            </div>
            <div className={styles.addressFormContent}>
                <div className={styles.formGrid}>
                    {isImporter && onImporterCompanyChange && (
                        <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                            <label>Nome da Empresa Importadora <span className={styles.required}>*</span></label>
                            <input
                                type="text"
                                value={importerCompanyName}
                                onChange={(e) => onImporterCompanyChange(e.target.value)}
                                className={styles.input}
                                placeholder="Nome da empresa importadora"
                                disabled={isSaving}
                            />
                        </div>
                    )}

                    <div className={styles.inputGroup}>
                        <label>Tipo de Endereço <span className={styles.required}>*</span></label>
                        <Select
                            styles={selectStyles}
                            options={typeAddressOptions}
                            value={selectedType}
                            onChange={(option) => handleChange('tipo', option ? option.value : '')}
                            placeholder="Selecione..."
                            isDisabled={isSaving}
                            isClearable
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>CEP <span className={styles.required}>*</span></label>
                        <input
                            type="text"
                            value={localAddress.cep || ''}
                            onChange={(e) => {
                                handleChange('cep', e.target.value);
                                handleFetchCep(e.target.value);
                            }}
                            className={styles.input}
                            maxLength={9}
                            placeholder="00000-000"
                            disabled={isSaving}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                        <label>Logradouro <span className={styles.required}>*</span></label>
                        <input
                            type="text"
                            value={localAddress.logradouro || ''}
                            onChange={(e) => handleChange('logradouro', e.target.value)}
                            className={styles.input}
                            placeholder="Rua, Avenida, etc."
                            disabled={isSaving}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Número</label>
                        <input
                            type="text"
                            value={localAddress.numero || ''}
                            onChange={(e) => handleChange('numero', e.target.value)}
                            className={styles.input}
                            placeholder="Nº"
                            disabled={isSaving}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Complemento</label>
                        <input
                            type="text"
                            value={localAddress.complemento || ''}
                            onChange={(e) => handleChange('complemento', e.target.value)}
                            className={styles.input}
                            placeholder="Apto, Bloco, etc."
                            disabled={isSaving}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Bairro</label>
                        <input
                            type="text"
                            value={localAddress.bairro || ''}
                            onChange={(e) => handleChange('bairro', e.target.value)}
                            className={styles.input}
                            placeholder="Bairro"
                            disabled={isSaving}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>UF</label>
                        <Select
                            styles={selectStyles}
                            options={allStates}
                            value={selectedUF}
                            onChange={(option) => {
                                handleChange('uf', option ? option.value : '');
                                handleChange('cidade', '');
                            }}
                            isLoading={loadingStates}
                            placeholder={country === 'BR' ? "Selecione o estado" : "Apenas Brasil"}
                            isDisabled={isSaving || country !== 'BR'}
                            isClearable
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Cidade</label>
                        <Select
                            styles={selectStyles}
                            options={allCities}
                            value={selectedCity}
                            onChange={(option) => handleChange('cidade', option ? option.value : '')}
                            isLoading={loadingCities}
                            placeholder={localAddress.uf ? "Selecione a cidade" : "Selecione o estado primeiro"}
                            isDisabled={isSaving || !localAddress.uf || loadingCities}
                            isClearable
                        />
                    </div>
                </div>

                {/* Contacts Section */}
                <div className={styles.contactsSection}>
                    <div className={styles.contactsSectionHeader}>
                        <span className={styles.contactsIcon}>👥</span>
                        <h5>Contatos do Endereço</h5>
                    </div>

                    {(localAddress.contacts || []).length > 0 && (
                        <div className={styles.contactList}>
                            {(localAddress.contacts || []).map((contact, index) => (
                                <div key={index} className={styles.contactItem}>
                                    <div className={styles.contactInfo}>
                                        <strong>{contact.name}</strong>
                                        {contact.cargo && (
                                            <span className={styles.contactRole}>
                                                - {rolesOptions.find(r => r.value === contact.cargo)?.label || contact.cargo}
                                            </span>
                                        )}
                                        {contact.email && (
                                            <span className={styles.contactEmail}> - {contact.email}</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveContact(index)}
                                        className={styles.removeContactButton}
                                        disabled={isSaving}
                                    >
                                        <FiX size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className={styles.contactForm}>
                        <div className={styles.contactFormRow}>
                            <div className={styles.contactFormGroup}>
                                <label>Nome</label>
                                <input
                                    type="text"
                                    value={newContact.name}
                                    onChange={(e) => handleContactChange('name', e.target.value)}
                                    className={styles.input}
                                    placeholder="Nome do contato"
                                    disabled={isSaving}
                                />
                            </div>
                            <div className={styles.contactFormGroup}>
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={newContact.email}
                                    onChange={(e) => handleContactChange('email', e.target.value)}
                                    className={styles.input}
                                    placeholder="email@exemplo.com"
                                    disabled={isSaving}
                                />
                            </div>
                            <div className={styles.contactFormGroup}>
                                <label>Cargo</label>
                                <Select
                                    styles={selectStyles}
                                    options={rolesOptions}
                                    value={rolesOptions.find(o => o.value === newContact.cargo) || null}
                                    onChange={(option) => handleContactChange('cargo', option ? option.value : '')}
                                    placeholder="Selecione..."
                                    isDisabled={isSaving}
                                    isClearable
                                />
                            </div>
                            <div className={styles.contactFormGroup} style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={handleAddContact}
                                    className={styles.addContactButton}
                                    disabled={isSaving || !newContact.name}
                                >
                                    <FiPlus size={16} /> Adicionar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.addressFormActions}>
                    <button
                        type="button"
                        onClick={onCancel}
                        className={styles.cancelButton}
                        disabled={isSaving}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={() => onSave(localAddress)}
                        className={styles.saveButton}
                        disabled={isSaving || !localAddress.logradouro || !localAddress.cep || !localAddress.tipo}
                    >
                        {isSaving ? (
                            <>
                                <span className={styles.buttonSpinner}></span>
                                Salvando...
                            </>
                        ) : (
                            saveText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddressForm;